import { apiAiAssistant } from "@/api/ai-assistant.api.ts";
import { apiFiles } from "@/api/file.api.ts";
import { AiAssistantMarkdown } from "@/components/ai-assistant/AiAssistantMarkdown.tsx";
import { AiAssistantMessage } from "@/components/ai-assistant/AiAssistantMessage.tsx";
import {
  createAiAssistantTransport,
  isAiAssistantBusyError,
  isAiAssistantLimitError,
} from "@/components/ai-assistant/ai-assistant-transport.ts";
import { FileItem } from "@/components/files/FileItem.tsx";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeeGoalsKeys } from "@/queries/employees/use-employee-goals.query.ts";
import { goalsKeys } from "@/queries/goals.query.ts";
import { aiAssistantKeys, useAiAssistantThread } from "@/queries/use-ai-assistant.query.ts";
import {
  AI_ASSISTANT_FILE_URL_PREFIX,
  AiAssistantUIMessage,
  ApiAiAssistantReplyStatus,
} from "@/types/ai-assistant.types.ts";
import { ApiErrors, ApiValidationError } from "@/types/api.types.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import { useChat } from "@ai-sdk/react";
import { Button, ButtonIcon, EmojiDisplay, FontAwesomeIcon, Stack, Text } from "@skailer-com/uikit";
import { useQueryClient } from "@tanstack/react-query";
import {
  getToolName,
  isToolUIPart,
  lastAssistantMessageIsCompleteWithApprovalResponses,
  UIMessage,
} from "ai";
import { ChangeEvent, FormEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const QUESTION_MAX_LENGTH = 8000;
const FILES_MAX_COUNT = 5;
// Совпадает с белым списком FileService на бэке.
const FILES_ACCEPT = ".xlsx,.xls,.csv,.pdf,.docx,.txt";

const FILE_ERROR_KEYS: Record<string, string> = {
  ai_assistant_file_type_not_supported: "ai:assistant_file_type_unsupported",
  ai_assistant_file_too_large: "ai:assistant_file_too_large",
};

const GOAL_WRITE_TOOLS = new Set([
  "create_goal",
  "update_goal",
  "create_key_result",
  "update_key_result",
  "checkin_goal",
]);

const hasChangedGoals = (message: UIMessage) =>
  message.parts.some(
    (part) =>
      isToolUIPart(part) &&
      part.state === "output-available" &&
      GOAL_WRITE_TOOLS.has(getToolName(part))
  );

interface AiAssistantChatProps {
  isOpen: boolean;
  threadId?: number;
  initialMessages: AiAssistantUIMessage[];
  initialReplyStatus?: ApiAiAssistantReplyStatus;
  onThreadSaved: (threadId: number) => void;
}

export function AiAssistantChat({
  isOpen,
  threadId,
  initialMessages,
  initialReplyStatus,
  onThreadSaved,
}: AiAssistantChatProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [{ transport, getThreadId, setThreadId }] = useState(() =>
    createAiAssistantTransport(threadId)
  );
  const [replyStatus, setReplyStatus] = useState(initialReplyStatus);
  const { messages, sendMessage, addToolApprovalResponse, status, error, stop } =
    useChat<AiAssistantUIMessage>({
      messages: initialMessages,
      transport,
      resume: initialReplyStatus === ApiAiAssistantReplyStatus.generating,
      sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
      onData: (part) => {
        if (part.type !== "data-thread") return;
        setThreadId(part.data.id);
        onThreadSaved(part.data.id);
        void queryClient.invalidateQueries({ queryKey: aiAssistantKeys.threads.queryKey });
      },
      onFinish: ({ message, isAbort }) => {
        setReplyStatus(
          isAbort ? ApiAiAssistantReplyStatus.stopped : ApiAiAssistantReplyStatus.done
        );
        if (!hasChangedGoals(message)) return;
        void queryClient.invalidateQueries({ queryKey: goalsKeys._def });
        void queryClient.invalidateQueries({ queryKey: employeeGoalsKeys._def });
      },
    });
  const { handleErrors } = useErrorHandle();
  const [input, setInput] = useState("");
  const [files, setFiles] = useState<ApiFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [fileError, setFileError] = useState<string>();
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const lastMessage = messages.at(-1);
  const isAwaitingApproval = !!lastMessage?.parts.some(
    (part) => isToolUIPart(part) && part.state === "approval-requested"
  );
  const isBusy = status === "submitted" || status === "streaming";
  const isTooLong = input.length > QUESTION_MAX_LENGTH;
  const finishedAnswer =
    status === "ready" && lastMessage?.role === "assistant" ? lastMessage : undefined;

  // Без wasOpenRef фокус уходит в поле ассистента при каждой загрузке с открытой панелью.
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) inputRef.current?.focus();
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, status]);

  const send = (text: string) => {
    if (!text || isBusy || isAwaitingApproval || isUploading) return;
    void sendMessage({
      text,
      files: files.map((file) => ({
        type: "file" as const,
        url: `${AI_ASSISTANT_FILE_URL_PREFIX}${file.uuid}`,
        mediaType: file.mime_type,
        filename: file.name,
      })),
    });
    setInput("");
    setFiles([]);
    setFileError(undefined);
  };

  const attachFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []).slice(0, FILES_MAX_COUNT - files.length);
    event.target.value = "";
    if (selected.length === 0) return;

    setFileError(undefined);
    setIsUploading(true);
    for (const file of selected) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("path", ApiFilePathTypes.ai_assistant);
      formData.append("is_public", "false");
      const { data, error } = await apiFiles.add(formData);
      if (data) {
        setFiles((current) => [...current, data]);
        continue;
      }
      const code =
        error?.status === ApiErrors.validationError
          ? (error as ApiValidationError).validationErrors[0]?.code
          : undefined;
      if (code && FILE_ERROR_KEYS[code]) {
        setFileError(t(FILE_ERROR_KEYS[code]));
      } else {
        handleErrors(error);
      }
    }
    setIsUploading(false);
  };

  const removeFile = async (file: ApiFile) => {
    setFiles((current) => current.filter((item) => item.uuid !== file.uuid));
    const { error } = await apiFiles.delete(file.uuid);
    if (error) handleErrors(error);
  };

  const stopReply = () => {
    void stop();
    const id = getThreadId();
    if (id) void apiAiAssistant.stopReply(id);
  };

  const retry = () => {
    const question = messages.filter((message) => message.role === "user").at(-1);
    if (question) void sendMessage({ parts: question.parts, messageId: question.id });
  };

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (!isTooLong) send(input.trim());
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <>
      <div ref={listRef} className={"flex-1 min-h-0 overflow-y-auto px-6 py-4"}>
        <Stack gap={"lg"} className={"min-h-full justify-end"}>
          {messages.length === 0 && <AiAssistantWelcome onSuggestion={send} />}
          {messages.map((message) => (
            <AiAssistantMessage
              key={message.id}
              message={message}
              onApprovalResponse={(response) => void addToolApprovalResponse(response)}
            />
          ))}
          {status === "submitted" && <AiAssistantSpinner />}
          {!isBusy && replyStatus === ApiAiAssistantReplyStatus.stopped && (
            <Text className={"text-secondary"}>{t("ai:assistant_stopped")}</Text>
          )}
          {!isBusy && replyStatus === ApiAiAssistantReplyStatus.interrupted && (
            <Stack direction={"horizontal"} items={"center"} gap={"sm"}>
              <Text className={"text-secondary"}>{t("ai:assistant_interrupted")}</Text>
              <Button size={"sm"} variant={"secondary"} onClick={retry}>
                {t("ai:assistant_retry")}
              </Button>
            </Stack>
          )}
          {error && (
            <Text role={"alert"} className={"text-danger"}>
              {isAiAssistantLimitError(error)
                ? t("ai:assistant_limit_exceeded")
                : isAiAssistantBusyError(error)
                  ? t("ai:assistant_busy")
                  : t("ai:assistant_error")}
            </Text>
          )}
        </Stack>
      </div>

      <div aria-live={"polite"} className={"sr-only"}>
        {finishedAnswer?.parts.map((part, index) =>
          part.type === "text" ? (
            <AiAssistantMarkdown key={index} isAnnouncement={true}>
              {part.text}
            </AiAssistantMarkdown>
          ) : null
        )}
      </div>

      <form onSubmit={submit} className={"shrink-0 px-6 pb-5"}>
        <div
          className={
            "rounded-md border border-dark/10 bg-light/80 p-2 shadow-xs transition-colors focus-within:border-brand-dark"
          }
        >
          {files.length > 0 && (
            <Stack gap={"xs"} className={"mb-2"}>
              {files.map((file) => (
                <FileItem
                  key={file.uuid}
                  file={file}
                  removeTitle={t("common:remove")}
                  onRemove={() => void removeFile(file)}
                />
              ))}
            </Stack>
          )}
          <textarea
            ref={inputRef}
            rows={2}
            value={input}
            placeholder={t("ai:assistant_input_placeholder")}
            aria-label={t("ai:assistant_input_placeholder")}
            className={
              "block max-h-40 w-full resize-none bg-transparent px-2 py-1 text-base outline-0 field-sizing-content placeholder:text-dark/50"
            }
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Stack direction={"horizontal"} justify={"between"} items={"center"}>
            <Stack direction={"horizontal"} items={"center"}>
              <input
                ref={fileInputRef}
                type={"file"}
                multiple={true}
                accept={FILES_ACCEPT}
                className={"hidden"}
                onChange={(event) => void attachFiles(event)}
              />
              <ButtonIcon
                type={"button"}
                variant={"plain"}
                aria-label={t("ai:assistant_attach_file")}
                disabled={files.length >= FILES_MAX_COUNT || isUploading}
                icon={
                  <FontAwesomeIcon
                    icon={
                      isUploading ? "fa-light fa-spinner-third fa-spin" : "fa-light fa-paperclip"
                    }
                  />
                }
                onClick={() => fileInputRef.current?.click()}
              />
              <Text size={"xs"} className={`px-2 ${isTooLong ? "text-danger" : "text-secondary"}`}>
                {input.length} / {QUESTION_MAX_LENGTH}
              </Text>
            </Stack>
            {isBusy ? (
              <ButtonIcon
                type={"button"}
                variant={"secondary"}
                aria-label={t("ai:assistant_stop")}
                // Пока ответ не пошёл, сервер мог ещё не начать генерацию: POST .../stop её не остановит.
                disabled={status !== "streaming"}
                icon={<FontAwesomeIcon icon={"fa-solid fa-stop"} />}
                onClick={stopReply}
              />
            ) : (
              <ButtonIcon
                type={"submit"}
                variant={"plain"}
                aria-label={t("ai:assistant_send")}
                disabled={!input.trim() || isTooLong || isAwaitingApproval || isUploading}
                icon={<FontAwesomeIcon icon={"fa-light fa-paper-plane-top"} />}
              />
            )}
          </Stack>
        </div>
        {isTooLong && (
          <Text role={"alert"} size={"xs"} className={"mt-2 block text-danger"}>
            {t("ai:assistant_question_too_long")}
          </Text>
        )}
        {fileError && (
          <Text role={"alert"} size={"xs"} className={"mt-2 block text-danger"}>
            {fileError}
          </Text>
        )}
        <Text size={"xs"} className={"mt-2 block text-center text-secondary"}>
          {t("ai:assistant_disclaimer")}
        </Text>
      </form>
    </>
  );
}

interface AiAssistantSavedChatProps extends Omit<
  AiAssistantChatProps,
  "threadId" | "initialMessages" | "initialReplyStatus"
> {
  threadId: number;
}

export function AiAssistantSavedChat({ threadId, ...props }: AiAssistantSavedChatProps) {
  const { data: thread, isPending } = useAiAssistantThread(threadId);

  if (thread) {
    return (
      <AiAssistantChat
        {...props}
        threadId={threadId}
        initialMessages={thread.messages}
        initialReplyStatus={thread.reply_status}
      />
    );
  }

  return <div className={"flex-1 p-4"}>{isPending && <AiAssistantSpinner />}</div>;
}

function AiAssistantSpinner() {
  return (
    <FontAwesomeIcon icon={"fa-light fa-spinner-third fa-spin"} className={"text-secondary"} />
  );
}

const SUGGESTIONS = [
  { icon: "fa-bullseye-arrow", key: "ai:assistant_suggestion_goals" },
  { icon: "fa-umbrella-beach", key: "ai:assistant_suggestion_time_off" },
  { icon: "fa-chart-line", key: "ai:assistant_suggestion_checkin" },
  { icon: "fa-chart-simple", key: "ai:assistant_suggestion_review" },
];

function AiAssistantWelcome({ onSuggestion }: { onSuggestion: (text: string) => void }) {
  const { t } = useTranslation();

  return (
    <Stack gap={"md"}>
      <span
        aria-hidden={true}
        className={"flex h-12 w-12 items-center justify-center rounded-sm bg-dark/5"}
      >
        <EmojiDisplay id={"sloth"} size={"lg"} />
      </span>
      <Stack gap={"xs"}>
        <Text className={"text-2xl font-semibold"}>{t("ai:assistant_greeting")}</Text>
        <Text className={"text-secondary"}>{t("ai:assistant_empty")}</Text>
      </Stack>
      <Stack gap={"sm"} items={"start"}>
        {SUGGESTIONS.map(({ icon, key }) => (
          <Button
            key={key}
            variant={"secondary"}
            leftIcon={<FontAwesomeIcon icon={`fa-light ${icon}`} />}
            onClick={() => onSuggestion(t(key))}
          >
            {t(key)}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
}
